/**
 * Evaluates a string as a function expression in a web browser environment.
 *
 * This utility first validates that the input is a function expression before attempting to evaluate it,
 * avoiding the risks associated with evaluating arbitrary JavaScript statements.
 * It is safer than using `eval()` directly, as it limits execution to function expressions only.
 *
 * The function is evaluated using the `Function` constructor, with limited isolation by binding
 * a provided context object as `this`. However, it does not prevent access to the global `window` object
 * or other browser APIs, and therefore must only be used with trusted, sanitized input.
 *
 * @param {string} functionString - A stringified function expression.
 * @param {Record<string, any>} context - Optional object to be bound as `this` within the function.
 * 
 * @returns {Function | undefined} The evaluated function, or undefined if evaluation fails.
 * 
 * 📝 We currently throw errors instead of returning them to preserve existing consumer behavior.
 *
 * For the next iteration:
 *
 * - Since the main routine is already wrapped in a try-catch block, we can consider returning error objects
 *   instead of throwing them, allowing callers to handle errors gracefully without halting application flow.
 *
 * - Plan to promote this utility to `ui-common`.
 * 
 */
export function safeFunctionEval(functionString: string, context: Record<string, any> = {}) {
  if (typeof functionString !== 'string') {
    throw new TypeError('Function string must be a string.');
  }

  const trimmed = functionString.trim();
  const baseErrorMsg = `[safeFunctionEval] Failed to parse function from string:\n${functionString}\n`;

  try {
    // Create a dynamic function with the context keys as arguments.
    // Wrap the input string in parentheses to ensure it is treated as an expression.
    // Intentional use of dynamic function construction — the function string is validated and scoped, with no exposure to end-user.
    const dynamicFunc = new Function(...Object.keys(context), `"use strict"; return (${trimmed});`); // NOSONAR

    // Call the dynamic function to get the actual function expression.
    const evaluatedFunction = dynamicFunc(Object.values(context));

    if (typeof evaluatedFunction !== 'function') {
      throw new Error(`${baseErrorMsg}\n Result is of type: ${typeof evaluatedFunction}, not a function.`);
    }

    return evaluatedFunction;
  } catch (error) {
    throw new Error(`${baseErrorMsg}\n Error: ${(error as Error).message}`);
  }
}